import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  Observable,
  startWith,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  ConversationIDsAPIResponseModel,
  IConversationIDsAPIResponse,
  IConversationIDsAPIResponseNormalized,
} from '../../shared/models/conversation-ids.model';
import { HttpClient } from '@angular/common/http';
import { EScreenSizeBreakPoint, SCREEN_SIZE_BREAKPOINTS, EViewMode } from '../configurations/application';
import { Store } from '@ngrx/store';
import { APPLICATION_ACTIONS } from '../../store/actions';
import { ActivatedRoute, EventType, NavigationStart, Router } from '@angular/router';
import { QUERY_PARAMS } from '../configurations/router';
import { APPLICATION_REDUCER } from '../../store/reducers/application.reducer';

@Injectable({
  providedIn: 'root',
})
export class ApplicationService {
  static URL_REDIRECT_LS_KEY: string = 'urlToRedirectAfterLogin';

  constructor(
    private readonly window: Window,
    private readonly location: Location,
    private readonly http: HttpClient,
    private readonly store: Store,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
  ) {
    this.initScreenChangesListener();
    this.initViewQueryParamListener();
  }

  get queryDataIds$(): Observable<string[]> {
    return this.activatedRoute.queryParams.pipe(
      withLatestFrom(this.store.select(APPLICATION_REDUCER.selectDataQueryParam)),
      map(([params, dataQueryParam]) => params[dataQueryParam.queryName] as string),
      filter(Boolean),
      map((value) => value.split(',')),
      distinctUntilChanged(),
    );
  }

  saveActiveUrlToStorage(): void {
    const urlToStartNextSession: string = this.location.path();
    if (urlToStartNextSession.length) {
      this.window.sessionStorage.setItem(ApplicationService.URL_REDIRECT_LS_KEY, urlToStartNextSession);
    }
  }

  getUrlFromStorage(): string | null {
    return this.window.sessionStorage.getItem(ApplicationService.URL_REDIRECT_LS_KEY);
  }

  removeUrlFromStorage(): void {
    this.window.sessionStorage.removeItem(ApplicationService.URL_REDIRECT_LS_KEY);
  }

  getConversationIDs(query: string, id: string): Observable<IConversationIDsAPIResponseNormalized> {
    return this.http
      .get<IConversationIDsAPIResponse>(`communication-ids/${query}/${id}`)
      .pipe(map((resp) => new ConversationIDsAPIResponseModel(resp)));
  }

  private initScreenChangesListener(): void {
    const debounceTimeMS = 100;

    fromEvent(this.window, 'resize')
      .pipe(
        startWith(null),
        debounceTime(debounceTimeMS),
        map(() => this.getBreakPointBySize(this.window.innerWidth, SCREEN_SIZE_BREAKPOINTS)),
        distinctUntilChanged(),
        tap((breakpoint) => this.store.dispatch(APPLICATION_ACTIONS.setScreenSizeBreakpoint({ breakpoint }))),
      )
      .subscribe();
  }

  private initViewQueryParamListener(): void {
    this.router.events
      .pipe(
        filter((event) => event.type === EventType.NavigationStart),
        map((event) => {
          const { url } = event as NavigationStart;
          const queryParamsString = url.substring(url.indexOf('?'));
          return new URLSearchParams(queryParamsString).get(QUERY_PARAMS.view) || null;
        }),
        distinctUntilChanged(),
        tap((viewMode: string | null) =>
          this.store.dispatch(APPLICATION_ACTIONS.setViewMode({ viewMode: viewMode as EViewMode | null })),
        ),
      )
      .subscribe();
  }

  private getBreakPointBySize(
    currentSize: number,
    breakpoints: Record<EScreenSizeBreakPoint, number>,
  ): EScreenSizeBreakPoint {
    for (const breakPoint in breakpoints) {
      const diff = currentSize - breakpoints[breakPoint as EScreenSizeBreakPoint];
      if (diff <= 0) {
        return breakPoint as EScreenSizeBreakPoint;
      }
    }
    return EScreenSizeBreakPoint.lg;
  }
}
